import React, { useState, useEffect } from 'react';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import UserDashboardHeader from '../../components/UserDashboardHeader';

interface Sound {
  id: string;
  name: string;
  file_path: string;
  duration_seconds: number;
  is_default?: boolean;
}

const NotificationTone: React.FC = () => {
  const colors = getThemeColors('dark');

  const [availableSounds, setAvailableSounds] = useState<Sound[]>([]);
  const [selectedSoundId, setSelectedSoundId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [playingSound, setPlayingSound] = useState<string | null>(null);
  const [testingSound, setTestingSound] = useState<string | null>(null);

  // Mock data for demonstration
  useEffect(() => {
    loadNotificationData();
  }, []);

  const loadNotificationData = async () => {
    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      const mockSounds: Sound[] = [
        { id: '1', name: 'Default Bell', file_path: '/sounds/bell.mp3', duration_seconds: 2.5, is_default: true },
        { id: '2', name: 'Chime', file_path: '/sounds/chime.mp3', duration_seconds: 1.8 },
        { id: '3', name: 'Alert', file_path: '/sounds/alert.mp3', duration_seconds: 3.2 },
        { id: '4', name: 'Soft Tone', file_path: '/sounds/soft.mp3', duration_seconds: 1.5 },
        { id: '5', name: 'Silent', file_path: '', duration_seconds: 0 },
      ];

      setAvailableSounds(mockSounds);
      setSelectedSoundId('1'); // Default selection
    } catch (error) {
      console.error('Failed to load notification data:', error);
    } finally {
      setLoading(false);
    }
  };

  const playSound = async (sound: Sound) => {
    try {
      if (playingSound === sound.id) {
        // Stop playing
        setPlayingSound(null);
        return;
      }

      // Handle silent sound
      if (!sound.file_path || sound.file_path === '') {
        alert('This is a silent notification tone - no sound will play.');
        return;
      }

      setPlayingSound(sound.id);

      // Simulate playing sound
      await new Promise(resolve => setTimeout(resolve, sound.duration_seconds * 1000));

      setPlayingSound(null);
      alert(`Played: ${sound.name} (${sound.duration_seconds}s)`);
    } catch (error) {
      console.error('Failed to play sound:', error);
      setPlayingSound(null);
      alert('Failed to play sound. Please try again.');
    }
  };

  const selectNotificationTone = async (soundId: string) => {
    try {
      setSelectedSoundId(soundId);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      alert('Notification tone updated successfully!');
    } catch (error) {
      console.error('Failed to update notification tone:', error);
      alert('Failed to update notification tone. Please try again.');
    }
  };

  const testNotificationSound = async (soundId: string) => {
    try {
      setTestingSound(soundId);
      // Simulate testing notification
      await new Promise(resolve => setTimeout(resolve, 2000));
      alert('Test notification sent! Check your notifications.');
    } catch (error) {
      console.error('Failed to test notification sound:', error);
      alert('Failed to test notification sound. Please try again.');
    } finally {
      setTestingSound(null);
    }
  };

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.background,
      paddingBottom: 80, // Space for tab bar
    },
    scrollableContent: {
      flex: 1,
      overflowY: 'auto',
      paddingTop: 80, // Space for fixed header
      paddingBottom: 80, // Space for fixed tab bar
    },
    content: {
      maxWidth: '900px',
      margin: '0 auto',
      padding: spacing.lg,
    },
    header: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.xl,
      padding: spacing.xl,
      marginBottom: spacing.xl,
      border: `1px solid ${colors.border}`,
      ...getShadowStyle('lg'),
      textAlign: 'center',
    },
    headerIcon: {
      fontSize: 48,
      marginBottom: spacing.md,
    },
    headerTitle: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    headerSubtitle: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
    },
    instructionsContainer: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.xl,
      padding: spacing.xl,
      marginBottom: spacing.xl,
      border: `1px solid ${colors.border}`,
      ...getShadowStyle('lg'),
    },
    instructionsContent: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: spacing.md,
    },
    instructionsIcon: {
      fontSize: 24,
      color: colors.primary,
      marginTop: spacing.xs,
    },
    instructionsText: {
      flex: 1,
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
      lineHeight: 1.5,
    },
    soundsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: spacing.lg,
    },
    soundItem: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.xl,
      padding: spacing.xl,
      border: `2px solid ${colors.border}`,
      ...getShadowStyle('lg'),
      transition: 'all 0.3s ease',
      cursor: 'pointer',
    },
    soundItemSelected: {
      borderColor: colors.primary,
      backgroundColor: colors.primary + '10',
      transform: 'scale(1.02)',
    },
    soundItemContent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    soundInfo: {
      flex: 1,
    },
    soundHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing.md,
      marginBottom: spacing.sm,
    },
    soundName: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
    },
    selectedIcon: {
      fontSize: 24,
      color: colors.success,
    },
    defaultBadge: {
      backgroundColor: colors.primary + '20',
      padding: `${spacing.xs} ${spacing.sm}`,
      borderRadius: borderRadius.md,
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.bold,
      color: colors.primary,
    },
    soundDuration: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
      marginBottom: spacing.xs,
    },
    soundDescription: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
      fontStyle: 'italic',
    },
    soundActions: {
      display: 'flex',
      gap: spacing.md,
    },
    actionButton: {
      width: 56,
      height: 56,
      borderRadius: 28,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      border: 'none',
      transition: 'all 0.2s ease',
      ...getShadowStyle('md'),
    },
    playButton: {
      backgroundColor: colors.primary + '20',
      color: colors.primary,
    },
    playButtonHover: {
      backgroundColor: colors.primary + '30',
      transform: 'scale(1.05)',
    },
    testButton: {
      backgroundColor: colors.secondary + '20',
      color: colors.secondary,
    },
    testButtonHover: {
      backgroundColor: colors.secondary + '30',
      transform: 'scale(1.05)',
    },
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: spacing.xxxl,
    },
    loadingSpinner: {
      width: 48,
      height: 48,
      border: `4px solid ${colors.border}`,
      borderTop: `4px solid ${colors.primary}`,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      marginBottom: spacing.lg,
    },
    loadingText: {
      fontSize: typography.fontSize.lg,
      color: colors.textSecondary,
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <UserDashboardHeader
          title="Notification Tones"
          subtitle="Choose your notification sound"
          showBackButton={true}
          showHomeButton={true}
        />

        <div style={styles.scrollableContent}>
          <div style={styles.content}>
            <div style={styles.loadingContainer}>
              <div style={styles.loadingSpinner} />
              <div style={styles.loadingText}>Loading notification tones...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Notification Tones"
        subtitle="Choose your notification sound"
        showBackButton={true}
        showHomeButton={true}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.headerIcon}>🔊</div>
            <h1 style={styles.headerTitle}>Notification Tones</h1>
            <p style={styles.headerSubtitle}>Choose your preferred notification sound</p>
          </div>

          {/* Instructions */}
          <div style={styles.instructionsContainer}>
            <div style={styles.instructionsContent}>
              <span style={styles.instructionsIcon}>ℹ️</span>
              <p style={styles.instructionsText}>
                Tap a tone to select it as your default notification sound. You can also tap the play button to preview each tone or use the test button to receive a real notification.
              </p>
            </div>
          </div>

          {/* Sound List */}
          <div style={styles.soundsList}>
            {availableSounds.map((sound) => {
              const isSelected = selectedSoundId === sound.id;
              const isPlaying = playingSound === sound.id;
              const isTesting = testingSound === sound.id;

              return (
                <div
                  key={sound.id}
                  style={{
                    ...styles.soundItem,
                    ...(isSelected ? styles.soundItemSelected : {}),
                  }}
                  onClick={() => selectNotificationTone(sound.id)}
                >
                  <div style={styles.soundItemContent}>
                    <div style={styles.soundInfo}>
                      <div style={styles.soundHeader}>
                        <span style={styles.soundName}>{sound.name}</span>
                        {isSelected && <span style={styles.selectedIcon}>✓</span>}
                        {sound.is_default && (
                          <span style={styles.defaultBadge}>Default</span>
                        )}
                      </div>

                      {sound.duration_seconds > 0 && (
                        <div style={styles.soundDuration}>
                          Duration: {sound.duration_seconds.toFixed(1)}s
                        </div>
                      )}

                      {sound.file_path === '' && (
                        <div style={styles.soundDescription}>
                          Silent notification
                        </div>
                      )}
                    </div>

                    <div style={styles.soundActions}>
                      {/* Play/Stop Button */}
                      <button
                        style={{
                          ...styles.actionButton,
                          ...styles.playButton,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          playSound(sound);
                        }}
                        title={isPlaying ? 'Stop' : 'Play'}
                      >
                        {isPlaying ? '⏹️' : '▶️'}
                      </button>

                      {/* Test Notification Button */}
                      <button
                        style={{
                          ...styles.actionButton,
                          ...styles.testButton,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          testNotificationSound(sound.id);
                        }}
                        disabled={isTesting}
                        title="Test Notification"
                      >
                        {isTesting ? '⏳' : '🔔'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationTone;